import { Box, Flex, Image, Stack } from '@chakra-ui/react';
import React from 'react';

import { Badge, Typography } from 'shared';

import { WidgetCardWrapper } from 'features/chat/components/ChatMessages/ChatMessage/Widgets/CardWrapper';
import { Widget } from 'features/chat/config/types/Message';

import {
  getChallengeFromWidget,
  getStatusBadgeTokens,
  getWidgetSubtitle,
  getWidgetTitle,
  resolveStatusText,
  toStartCase,
  getWidgetMeta,
  WidgetMeta,
} from './widgetMeta';

type ChallengeWidgetProps = {
  widget: Widget;
  meta?: WidgetMeta;
};

export const ChallengeWidget = ({ widget, meta }: ChallengeWidgetProps) => {
  const computedMeta = meta ?? getWidgetMeta(widget);
  const challenge = getChallengeFromWidget(widget);
  const title =
    getWidgetTitle(widget, computedMeta) ??
    toStartCase(computedMeta.entityLabel);
  const subtitle = getWidgetSubtitle(widget, computedMeta);
  const statusTokens = getStatusBadgeTokens(computedMeta.statusVariant);
  const statusText = resolveStatusText(widget, computedMeta);
  const emoji = challenge?.emoji;

  return (
    <WidgetCardWrapper>
      <Stack gap="12px" alignItems="flex-start">
        <Badge colorPalette="lightGray">{computedMeta.entityLabel}</Badge>

        <Flex gap="12px" alignItems="center" width="100%">
          <Box
            width="36px"
            height="36px"
            borderRadius="8px"
            overflow="hidden"
            bg="gray.200"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {challenge?.picture ? (
              <Image
                src={challenge.picture}
                alt={challenge.title || 'Challenge cover'}
                width="100%"
                height="100%"
                objectFit="cover"
              />
            ) : (
              <Typography fontSize="20px" lineHeight="20px">
                {emoji || computedMeta.entityLabel.charAt(0)}
              </Typography>
            )}
          </Box>

          <Stack gap="6px" flex="1" minWidth="0">
            <Typography
              type="meta"
              lineHeight="normal"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="gray"
                lineHeight="normal"
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        </Flex>

        <Badge
          bg={statusTokens.bg}
          color={statusTokens.color}
          borderRadius="12px"
        >
          {statusText}
        </Badge>
      </Stack>
    </WidgetCardWrapper>
  );
};
